import { Sparkle, CurrencyCircleDollar, Fire, ArrowRight, CheckCircle } from '@phosphor-icons/react';
import { format, isAfter } from 'date-fns';
import { parseDate, getCurrencySymbol } from './helpers';

export const HeroBriefing = ({ ai, briefingImage, topPriority, refreshing, cachedAt }) => {
  return (
    <div className="rounded-2xl overflow-hidden bg-[#fafbfc] border border-[#f0f1f3]" data-testid="hero-briefing">
      <div className="flex flex-col lg:flex-row">
        {/* Left: Briefing Image */}
        {briefingImage && (
          <div className="lg:w-[45%] flex-shrink-0 relative bg-[#f0f1f3]" data-testid="briefing-image">
            <img
              key={cachedAt || briefingImage.slice(-20)}
              src={`data:image/png;base64,${briefingImage}`}
              alt="Today's Focus"
              className={`w-full h-full object-contain transition-opacity duration-500 ${refreshing ? 'opacity-40' : 'opacity-100'}`}
            />
            {refreshing && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-white/80 backdrop-blur-sm rounded-xl px-4 py-2.5 flex items-center gap-2 shadow-lg">
                  <div className="w-4 h-4 border-2 border-[#2563eb] border-t-transparent rounded-full animate-spin" />
                  <span className="text-xs font-semibold text-[#2563eb]">Generating new image...</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Right: Priority + Briefing Text */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Do This First */}
          <PrioritySection topPriority={topPriority} />

          {/* Briefing Text */}
          <div className="px-5 py-4 flex-1">
            <div className="flex items-center gap-1.5 mb-2.5">
              <Sparkle size={14} className="text-[#005bc1]" weight="fill" />
              <span className="text-[#005bc1] text-[9px] font-bold uppercase tracking-widest">AI Briefing</span>
            </div>

            <p className="text-[#2d3338] text-xs leading-relaxed mb-3">{ai.briefing || 'Loading your briefing...'}</p>

            {ai.priorities && ai.priorities.length > 0 && (
              <div className="space-y-1.5 mb-3">
                {ai.priorities.slice(0, 3).map((p, i) => (
                  <div key={i} className="flex items-start gap-2 text-[11px] text-[#596065]">
                    <span className="w-4 h-4 rounded-full bg-[#005bc1] text-white text-[8px] font-bold flex-shrink-0 flex items-center justify-center mt-0.5">{i + 1}</span>
                    <span className="leading-relaxed">{p}</span>
                  </div>
                ))}
              </div>
            )}

            {ai.money_insight && (
              <div className="bg-[#fef9c3] border border-[#fde68a] rounded-lg p-2 flex items-start gap-2 mb-2">
                <CurrencyCircleDollar size={14} className="text-[#92400e] flex-shrink-0 mt-0.5" weight="duotone" />
                <p className="text-[11px] text-[#92400e] leading-relaxed">{ai.money_insight}</p>
              </div>
            )}

            {ai.signoff && (
              <p className="text-[10px] text-[#acb3b8] italic">&mdash; {ai.signoff}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const PrioritySection = ({ topPriority }) => {
  if (!topPriority?.task) {
    return (
      <div className="bg-[#f0fdf4] px-5 py-3 flex items-center gap-2.5 border-b border-[#f0f1f3]" data-testid="priority-card-empty">
        <CheckCircle size={18} className="text-[#0f6b3a]" weight="duotone" />
        <span className="font-semibold text-[#0f6b3a] text-xs">All clear! No urgent tasks.</span>
      </div>
    );
  }

  const sym = getCurrencySymbol(topPriority.task.currency || 'USD');
  const due = parseDate(topPriority.task.due_date);
  const isOverdue = due && isAfter(new Date(), due);

  return (
    <div className="bg-[#1e1e2e] px-5 py-4 relative overflow-hidden" data-testid="priority-card">
      <div className="absolute -top-4 -right-4 w-20 h-20 bg-[#f59e0b] rounded-full opacity-10" />
      <div className="relative z-10">
        <div className="flex items-center gap-1.5 mb-2">
          <Fire size={14} className="text-[#f59e0b]" weight="fill" />
          <span className="text-[#f59e0b] text-[9px] font-bold uppercase tracking-widest">Do This First</span>
        </div>

        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-1.5">
          <h3 className="text-white font-bold text-sm leading-snug" data-testid="priority-title">
            {topPriority.task.title}
          </h3>
          <div className="flex items-center gap-2 text-[10px]">
            {topPriority.task.amount > 0 && (
              <span className="text-[#fbbf24] font-semibold">{sym}{topPriority.task.amount?.toFixed(2)}</span>
            )}
            {due && (
              <span className={`font-semibold ${isOverdue ? 'text-[#ef4444]' : 'text-[#a6adb4]'}`}>
                {format(due, 'MMM d')}
                {isOverdue && <span className="ml-1 text-[8px] bg-[#ef4444]/20 px-1 py-0.5 rounded uppercase">Overdue</span>}
              </span>
            )}
            <span className="text-[#a6adb4] capitalize">{topPriority.task.category}</span>
          </div>
        </div>

        <p className="text-[#a6adb4] text-[11px] leading-relaxed mb-2 line-clamp-2">
          {topPriority.ai_reason || 'This is your most urgent item.'}
        </p>

        <a href="/inbox" className="inline-flex items-center gap-1 bg-[#f59e0b] text-[#1e1e2e] font-bold text-[10px] px-3 py-1.5 rounded-md hover:bg-[#fbbf24] transition-colors" data-testid="priority-action">
          Take Action <ArrowRight size={12} weight="bold" />
        </a>
      </div>
    </div>
  );
};
